/**
 * Site Configuration
 * Centralized configuration for SEO, contact info, and site metadata
 */

// Contact constants
const CONTACT_EMAIL =
  process.env.NEXT_PUBLIC_CONTACT_EMAIL || "nick.leija@leijainsurancegroup.com";
const CONTACT_PHONE = process.env.NEXT_PUBLIC_CONTACT_PHONE || "(512) 690-0075";
const CONTACT_PHONE_HREF = `tel:${CONTACT_PHONE.replace(/[^0-9]/g, "")}`;
const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL || "https://leijainsurancegroup.com";

export const siteConfig = {
  // Business Information
  name: "Leija Insurance Group",
  tagline: "Small Business Benefits That Actually Work",
  description:
    "Military veteran Nicholas Leija helps San Marcos, Kyle, and Buda businesses save $300-$500 per employee while providing better benefits. Licensed in TX, CA, NV. Available 24/7.",
  url: SITE_URL,

  // Contact Information
  contact: {
    phone: CONTACT_PHONE,
    phoneFormatted: CONTACT_PHONE,
    phoneHref: CONTACT_PHONE_HREF,
    email: CONTACT_EMAIL,
    emailHref: `mailto:${CONTACT_EMAIL}`,
    availability: "Available 24/7 - Evenings & Weekends",
  },

  // Location Information
  location: {
    areas: ["San Marcos", "Kyle", "Buda"],
    areasFormatted: "San Marcos, Kyle, and Buda",
    region: "Greater Austin Area",
    state: "Texas",
    description: "Serving San Marcos, Kyle, Buda and surrounding areas",
  },

  // Licenses & Credentials
  credentials: {
    licenses: ["TX", "CA", "NV"],
    licensesFormatted: "Licensed in TX, CA, NV",
    experience: "2.5+ Years",
    military: {
      branch: "U.S. Army",
      yearsServed: "2012-2020",
      role: "AV Technician & IT Specialist",
    },
  },

  // Key Metrics
  metrics: {
    averageSavings: "$300-$500",
    savingsDescription: "Average monthly savings per employee",
    employeeRange: "1-50",
    employeeDescription: "Employee businesses served",
    taxCredits: "Up to $16,500",
    taxCreditsDescription: "In tax credits for new retirement plans",
  },

  // Services
  services: [
    {
      slug: "group-health",
      name: "Group Health Insurance",
      shortName: "Group Health",
      description:
        "Custom plans from top providers like Blue Cross Blue Shield, United Healthcare, Cigna",
      carriers: [
        "Blue Cross Blue Shield",
        "United Healthcare",
        "Cigna",
        "Aetna",
      ],
    },
    {
      slug: "retirement",
      name: "Retirement Planning",
      shortName: "Retirement",
      description: "401k, SEP/SIMPLE IRA with tax credits for new plans",
      types: ["401(k)", "SEP IRA", "SIMPLE IRA"],
    },
    {
      slug: "life",
      name: "Life Insurance",
      shortName: "Life",
      description:
        "Group and individual policies from National Life Group, Mutual of Omaha",
      carriers: ["National Life Group", "Mutual of Omaha"],
    },
    {
      slug: "legal",
      name: "Legal Services",
      shortName: "Legal",
      description: "LegalShield + IDShield for comprehensive legal protection",
      providers: ["LegalShield", "IDShield"],
    },
    {
      slug: "supplemental",
      name: "Supplemental Coverage",
      shortName: "Supplemental",
      description: "Accident, disability, dental - options for every budget",
      types: ["Accident", "Disability", "Dental"],
    },
    {
      slug: "consulting",
      name: "Business Consulting",
      shortName: "Consulting",
      description:
        "Health Reimbursement Arrangements, tax optimization strategies",
      services: ["HRA Setup", "Section 125 Plans", "Compliance Guidance"],
    },
  ],

  // SEO Keywords
  seo: {
    keywords: [
      "group health insurance San Marcos",
      "small business benefits Kyle Texas",
      "retirement plans Buda",
      "business insurance broker Austin",
      "Nicholas Leija insurance",
      "group health insurance Texas",
      "401k small business",
      "SEP IRA Texas",
      "business benefits consultant",
    ],

    // Structured Data
    organization: {
      "@context": "https://schema.org",
      "@type": "InsuranceAgency",
      name: "Leija Insurance Group",
      description:
        "Small business insurance and benefits consulting in San Marcos, Kyle, and Buda, Texas",
      founder: {
        "@type": "Person",
        name: "Nicholas Leija",
        jobTitle: "Licensed Insurance Agent & Benefits Specialist",
        description:
          "U.S. Army Veteran (2012-2020), Licensed Life, Health, Accident & HMO Agent",
      },
      areaServed: {
        "@type": "GeoCircle",
        geoMidpoint: {
          "@type": "GeoCoordinates",
          latitude: "29.8833",
          longitude: "-97.9414",
        },
        geoRadius: "50000",
        description: "San Marcos, Kyle, Buda, and Greater Austin Area",
      },
      telephone: CONTACT_PHONE,
      email: CONTACT_EMAIL,
    },

    // Local Business Schema
    localBusiness: {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Leija Insurance Group",
      image: `${SITE_URL}/nicholas-hero.png`,
      telephone: CONTACT_PHONE,
      email: CONTACT_EMAIL,
      address: {
        "@type": "PostalAddress",
        addressRegion: "TX",
        addressLocality: "San Marcos",
        addressCountry: "US",
      },
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
      priceRange: "$$",
    },
  },

  // Social & Links
  links: {
    home: "/",
    about: "#about",
    services: "#services",
    process: "#process",
    testimonials: "#testimonials",
    faq: "#faq",
    contact: "#contact",
  },

  // Process Steps
  process: {
    totalMeetings: 4,
    timeline: "2-3 weeks",
    steps: [
      {
        number: 1,
        name: "Discovery Meeting",
        duration: "30-45 minutes",
        description: "Understanding your business goals and current situation",
      },
      {
        number: 2,
        name: "Education Session",
        duration: "45-60 minutes",
        description: "Financial education and benefit options overview",
      },
      {
        number: 3,
        name: "Custom Proposals",
        duration: "60 minutes",
        description: "3+ tailored solutions based on your specific needs",
      },
      {
        number: 4,
        name: "Implementation",
        duration: "Ongoing support",
        description: "Seamless setup with 24/7 support",
      },
    ],
  },

  // Value Propositions
  values: [
    {
      name: "Military Precision",
      description: "Attention to detail and follow-through you can count on",
    },
    {
      name: "Personal Service",
      description:
        "Available 24/7 because your needs don't follow business hours",
    },
    {
      name: "Community Focus",
      description:
        "Supporting local teachers and first responders through special programs",
    },
  ],
} as const;

// Type exports for TypeScript autocomplete
export type SiteConfig = typeof siteConfig;
export type Service = (typeof siteConfig.services)[number];
